'use client';

import { useState, useEffect } from 'react';
import {
  Shield,
  Save,
  Loader2,
  AlertCircle,
  CheckCircle,
  Key,
  Globe,
  Copy,
  Eye,
  EyeOff,
  Lock,
  ChevronRight,
  Smartphone,
  X,
  Plus,
  AlertTriangle,
} from 'lucide-react';
import { getSamlCallbackUrl, getSamlEntityId, getSamlBaseUrl } from '@/lib/samlHelper';
import { useTranslation } from '@/providers/I18nProvider';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { useAgent } from '@/hooks/useAgent';
import { useToast } from '@/hooks/useToast';
import { useQueryClient } from '@tanstack/react-query';

interface SecuritySettingsProps {
  agentId: string;
  agentName: string;
  initialSettings: {
    auth?: 'PUBLIC' | 'SECURE';
    authMethod?: 'PASSWORD' | 'SAML' | 'BOTH' | 'PUBLIC';
    samlConfig?: SAMLConfig;
    twoFactorConfig?: {
      required: boolean;
      rememberDeviceDays: number;
    };
  };
  onSave?: () => void;
  isClientApi?: boolean;
}

interface SAMLConfig {
  ssoUrl: string;
  issuer: string;
  certificate: string;
  allowedDomains: string[];
  defaultRole: 'user' | 'admin';
  attributeMapping: {
    email: string;
    firstName: string;
    lastName: string;
    avatar?: string;
  };
}

export default function SecuritySettings({ agentId, agentName, initialSettings, onSave, isClientApi = false }: SecuritySettingsProps) {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const [savingSaml, setSavingSaml] = useState(false);
  const [savingAuth, setSavingAuth] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showCertificate, setShowCertificate] = useState(false);
  const [savedSamlConfig, setSavedSamlConfig] = useState<SAMLConfig | null>(initialSettings.samlConfig || null);

  const [authMethod, setAuthMethod] = useState<'PASSWORD' | 'SAML' | 'BOTH' | 'PUBLIC'>(initialSettings.authMethod || 'PASSWORD');
  const [samlConfig, setSamlConfig] = useState<SAMLConfig>(
    initialSettings.samlConfig || {
      ssoUrl: '',
      issuer: '',
      certificate: '',
      allowedDomains: [],
      defaultRole: 'user',
      attributeMapping: {
        email: 'email',
        firstName: 'firstName',
        lastName: 'lastName',
        avatar: 'picture',
      },
    }
  );

  const [domainInput, setDomainInput] = useState('');

  // 2FA Configuration state
  const [twoFactorConfig, setTwoFactorConfig] = useState({
    required: initialSettings.twoFactorConfig?.required || false,
    rememberDeviceDays: initialSettings.twoFactorConfig?.rememberDeviceDays ?? 30,
  });
  const [saving2FA, setSaving2FA] = useState(false);

  // CORS Configuration state
  const [corsOrigins, setCorsOrigins] = useState<string[]>([]);
  const [newOrigin, setNewOrigin] = useState('');
  const [savingCors, setSavingCors] = useState(false);

  // Use feature flags hook
  const { chatSSOEnabled, chat2FAEnabled, isLoading: loadingFlags } = useFeatureFlags(agentId);

  // Use agent hook for mutation and to get organizationId
  const { updateAgent, agent, configDoc } = useAgent(agentId);
  const organizationId = configDoc?.organizationId;

  // Load CORS origins from agent config
  useEffect(() => {
    if (configDoc?.corsAllowedOrigins) {
      setCorsOrigins(configDoc.corsAllowedOrigins);
    }
  }, [configDoc]);

  // Track if SAML config has changed
  const hasSamlChanges =
    JSON.stringify(samlConfig) !==
    JSON.stringify(
      savedSamlConfig || {
        ssoUrl: '',
        issuer: '',
        certificate: '',
        allowedDomains: [],
        defaultRole: 'user',
        attributeMapping: {
          email: 'email',
          firstName: 'firstName',
          lastName: 'lastName',
          avatar: 'picture',
        },
      }
    );

  const handleAuthMethodChange = async (newMethod: 'PASSWORD' | 'SAML' | 'BOTH' | 'PUBLIC') => {
    setAuthMethod(newMethod);
    setSavingAuth(true);
    setError('');

    try {
      await updateAgent.mutateAsync({
        authMethod: !chatSSOEnabled && (newMethod === 'SAML' || newMethod === 'BOTH') ? 'PASSWORD' : newMethod,
      });

      showToast(t('admin.security.authMethodSaved'), 'success');

      // Invalidate cache to refresh agent data
      await queryClient.invalidateQueries({ queryKey: ['agent', agentId] });

      // Call the onSave callback if provided
      if (onSave) {
        onSave();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('admin.security.errors.saveFailed'));
      showToast(err instanceof Error ? err.message : t('admin.security.errors.saveFailed'), 'error');
      // Revert on error
      setAuthMethod(initialSettings.authMethod || 'PASSWORD');
    } finally {
      setSavingAuth(false);
    }
  };

  const handleSaveSaml = async () => {
    try {
      setSavingSaml(true);
      setError('');

      await updateAgent.mutateAsync({
        samlConfig: chatSSOEnabled ? samlConfig : null,
      });

      showToast(t('admin.security.samlSaved'), 'success');
      
      // Update saved config to reset change detection
      setSavedSamlConfig(samlConfig);

      // Invalidate cache to refresh agent data
      await queryClient.invalidateQueries({ queryKey: ['agent', agentId] });

      // Call the onSave callback if provided
      if (onSave) {
        onSave();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('admin.security.errors.saveFailed'));
      showToast(err instanceof Error ? err.message : t('admin.security.errors.saveFailed'), 'error');
    } finally {
      setSavingSaml(false);
    }
  };

  const handleSave2FA = async (config: { required: boolean; rememberDeviceDays: number }) => {
    setSaving2FA(true);
    setError('');

    try {
      await updateAgent.mutateAsync({
        twoFactorConfig: config,
      });

      showToast(t('admin.security.twoFactorSaved'), 'success');

      // Invalidate cache to refresh agent data
      await queryClient.invalidateQueries({ queryKey: ['agent', agentId] });

      // Call the onSave callback if provided
      if (onSave) {
        onSave();
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : t('admin.security.errors.saveFailed');
      setError(errorMessage);
      showToast(errorMessage, 'error');
    } finally {
      setSaving2FA(false);
    }
  };

  const handleToggle2FA = async (enabled: boolean) => {
    const newConfig = { ...twoFactorConfig, required: enabled };
    setTwoFactorConfig(newConfig);
    await handleSave2FA(newConfig);
  };

  const handleSetRememberDays = async (days: number) => {
    const newConfig = { ...twoFactorConfig, rememberDeviceDays: days };
    setTwoFactorConfig(newConfig);
    await handleSave2FA(newConfig);
  };

  const addDomain = () => {
    if (domainInput && !samlConfig.allowedDomains.includes(domainInput)) {
      setSamlConfig({
        ...samlConfig,
        allowedDomains: [...samlConfig.allowedDomains, domainInput],
      });
      setDomainInput('');
    }
  };

  const removeDomain = (domain: string) => {
    setSamlConfig({
      ...samlConfig,
      allowedDomains: samlConfig.allowedDomains.filter((d) => d !== domain),
    });
  };

  const copyMetadataUrl = () => {
    const metadataUrl = `${getSamlBaseUrl()}/api/chat/agents/${agentId}/saml/metadata`;
    navigator.clipboard.writeText(metadataUrl);
    setSuccess(t('admin.security.metadataCopied'));
    setTimeout(() => setSuccess(''), 2000);
  };

  const copyAcsUrl = () => {
    const acsUrl = getSamlCallbackUrl(agentId);
    navigator.clipboard.writeText(acsUrl);
    setSuccess(t('admin.security.acsCopied'));
    setTimeout(() => setSuccess(''), 2000);
  };

  const copyEntityId = () => {
    const entityId = getSamlEntityId(agentId);
    navigator.clipboard.writeText(entityId);
    setSuccess(t('admin.security.entityCopied'));
    setTimeout(() => setSuccess(''), 2000);
  };

  // CORS handlers
  const handleAddOrigin = async () => {
    if (!newOrigin.trim()) return;

    // Validate origin format
    if (newOrigin !== '*' && !newOrigin.match(/^https?:\/\/.+/)) {
      showToast('Invalid origin format. Use * or http(s)://domain', 'error');
      return;
    }

    if (corsOrigins.includes(newOrigin)) {
      showToast('Origin already exists', 'error');
      return;
    }

    const newOrigins = [...corsOrigins, newOrigin];
    setCorsOrigins(newOrigins);
    setNewOrigin('');
    setSavingCors(true);

    try {
      await updateAgent.mutateAsync({ corsAllowedOrigins: newOrigins });
      showToast('CORS origins saved successfully', 'success');
      await queryClient.invalidateQueries({ queryKey: ['agent', agentId] });
    } catch (error) {
      console.error('Failed to save CORS origins:', error);
      showToast('Failed to save CORS origins', 'error');
      setCorsOrigins(corsOrigins); // Revert
    } finally {
      setSavingCors(false);
    }
  };

  const handleRemoveOrigin = async (origin: string) => {
    const newOrigins = corsOrigins.filter((o) => o !== origin);
    setCorsOrigins(newOrigins);
    setSavingCors(true);

    try {
      await updateAgent.mutateAsync({ corsAllowedOrigins: newOrigins });
      showToast('CORS origins saved successfully', 'success');
      await queryClient.invalidateQueries({ queryKey: ['agent', agentId] });
    } catch (error) {
      console.error('Failed to save CORS origins:', error);
      showToast('Failed to save CORS origins', 'error');
      setCorsOrigins([...corsOrigins, origin]); // Revert
    } finally {
      setSavingCors(false);
    }
  };

  // Show skeleton loader while loading feature flags
  if (loadingFlags) {
    return (
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-4xl mx-auto p-8">
          {/* Header skeleton */}
          <div className="mb-8">
            <div className="h-8 w-48 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse mb-2"></div>
            <div className="h-4 w-96 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse"></div>
          </div>

          {/* Settings sections skeleton */}
          <div className="space-y-8">
            {/* Authentication Method skeleton */}
            <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800 p-6">
              <div className="flex items-center gap-2 mb-6">
                <div className="h-5 w-5 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
                <div className="h-6 w-48 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse"></div>
              </div>
              <div className="space-y-4">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <div className="h-5 w-5 bg-gray-200 dark:bg-gray-700 rounded-full animate-pulse mt-0.5"></div>
                      <div className="flex-1">
                        <div className="h-5 w-32 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse mb-2"></div>
                        <div className="h-4 w-64 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse"></div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* SAML Configuration skeleton */}
            <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800 p-6">
              <div className="flex items-center gap-2 mb-6">
                <div className="h-5 w-5 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
                <div className="h-6 w-40 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse"></div>
              </div>

              {/* Service Provider Info skeleton */}
              <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 mb-6">
                <div className="h-5 w-48 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse mb-4"></div>
                <div className="space-y-3">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="flex items-center justify-between">
                      <div className="h-4 w-24 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse"></div>
                      <div className="flex items-center gap-2">
                        <div className="h-4 w-64 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse"></div>
                        <div className="h-8 w-8 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* SAML Fields skeleton */}
              <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                  <div key={i}>
                    <div className="h-4 w-32 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse mb-2"></div>
                    <div className="h-10 w-full bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse"></div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Save Button skeleton */}
          <div className="mt-8 flex justify-end">
            <div className="h-10 w-32 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="max-w-4xl mx-auto p-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">{t('admin.security.title')}</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{t('admin.security.description')}</p>
        </div>

        {/* Settings sections */}
        <div className="space-y-8">
          {/* Alerts */}
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-red-500 mt-0.5" />
              <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
            </div>
          )}
          {success && (
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 flex items-start gap-3">
              <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
              <p className="text-sm text-green-800 dark:text-green-200">{success}</p>
            </div>
          )}

          {/* CORS Settings Section - Only for WIDGET type */}
          {isClientApi && (
            <section className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800 p-6">
              <div className="flex items-center gap-2 mb-4">
                <Globe className="h-5 w-5 text-gray-400" />
                <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">CORS Settings</h2>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                Configure allowed origins for widget embedding. Use * to allow all origins (not recommended for production).
              </p>

              {/* Add new origin */}
              <div className="mb-4">
                <label className="text-sm font-medium text-gray-900 dark:text-gray-100 block mb-2">Add Allowed Origin</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newOrigin}
                    onChange={(e) => setNewOrigin(e.target.value)}
                    placeholder="https://example.com or *"
                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white text-sm"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        handleAddOrigin();
                      }
                    }}
                    disabled={savingCors}
                  />
                  <button
                    onClick={handleAddOrigin}
                    disabled={savingCors || !newOrigin.trim()}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                  >
                    {savingCors ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                    Add
                  </button>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Examples: https://app.example.com, http://localhost:3000, *</p>
              </div>

              {/* List of allowed origins */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-900 dark:text-gray-100 block mb-2">Allowed Origins ({corsOrigins.length})</label>
                {corsOrigins.length === 0 ? (
                  <div className="text-sm text-gray-500 dark:text-gray-400 italic p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                    No origins configured. The widget will not be accessible from any domain.
                  </div>
                ) : (
                  <div className="space-y-2">
                    {corsOrigins.map((origin) => (
                      <div key={origin} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                        <span className="text-sm font-mono text-gray-700 dark:text-gray-300">{origin}</span>
                        <button
                          onClick={() => handleRemoveOrigin(origin)}
                          disabled={savingCors}
                          className="p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors"
                          title="Remove origin"
                        >
                          <X className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Security warning */}
              {corsOrigins.includes('*') && (
                <div className="mt-4 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400 mt-0.5" />
                    <div className="text-sm text-amber-700 dark:text-amber-300">
                      <strong>Security Warning:</strong> Using * allows any website to embed your widget. This is not recommended for production use.
                    </div>
                  </div>
                </div>
              )}
            </section>
          )}
          {/* Authentication Method */}
          <section className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800 p-6">
            <div className="flex items-center gap-2 mb-4">
              <Shield className="h-5 w-5 text-gray-400" />
              <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">{t('admin.security.authMethod.title')}</h2>
            </div>

            {/* WIDGET Info Box */}
            {isClientApi && (
              <div className="mb-4 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                <div className="flex">
                  <AlertCircle className="h-5 w-5 text-blue-400 flex-shrink-0" />
                  <div className="ml-3">
                    <p className="text-sm text-blue-700 dark:text-blue-300">
                      Chat API agents are meant to power widgets that don't require end user authentication. The authentication settings below only
                      apply to admin users who can customize the widget configuration.
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-3">
              {!isClientApi && (
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="radio"
                    name="authMethod"
                    value="PUBLIC"
                    checked={authMethod === 'PUBLIC'}
                    onChange={(e) => !savingAuth && handleAuthMethodChange(e.target.value as 'PUBLIC')}
                    disabled={savingAuth}
                    className="mt-1 disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                  <div>
                    <div className="font-medium text-gray-900 dark:text-gray-100">{t('admin.security.authMethod.public')}</div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">{t('admin.security.authMethod.publicDescription')}</div>
                  </div>
                </label>
              )}

              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="radio"
                  name="authMethod"
                  value="PASSWORD"
                  checked={authMethod === 'PASSWORD'}
                  onChange={(e) => !savingAuth && handleAuthMethodChange(e.target.value as 'PASSWORD')}
                  disabled={savingAuth}
                  className="mt-1 disabled:opacity-50 disabled:cursor-not-allowed"
                />
                <div>
                  <div className="font-medium text-gray-900 dark:text-gray-100">{t('admin.security.authMethod.passwordOnly')}</div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">{t('admin.security.authMethod.passwordDescription')}</div>
                </div>
              </label>

              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="radio"
                  name="authMethod"
                  value="SAML"
                  checked={authMethod === 'SAML'}
                  onChange={(e) => chatSSOEnabled && !savingAuth && handleAuthMethodChange(e.target.value as 'SAML')}
                  disabled={!chatSSOEnabled || savingAuth}
                  className="mt-1 disabled:opacity-50 disabled:cursor-not-allowed"
                />
                <div className={!chatSSOEnabled ? 'opacity-50' : ''}>
                  <div className="font-medium text-gray-900 dark:text-gray-100 flex items-center gap-2">
                    {t('admin.security.authMethod.samlOnly')}
                    {!chatSSOEnabled && <Lock className="h-3 w-3 text-amber-600 dark:text-amber-400" />}
                  </div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">{t('admin.security.authMethod.samlDescription')}</div>
                </div>
              </label>

              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="radio"
                  name="authMethod"
                  value="BOTH"
                  checked={authMethod === 'BOTH'}
                  onChange={(e) => chatSSOEnabled && !savingAuth && handleAuthMethodChange(e.target.value as 'BOTH')}
                  disabled={!chatSSOEnabled || savingAuth}
                  className="mt-1 disabled:opacity-50 disabled:cursor-not-allowed"
                />
                <div className={!chatSSOEnabled ? 'opacity-50' : ''}>
                  <div className="font-medium text-gray-900 dark:text-gray-100 flex items-center gap-2">
                    {t('admin.security.authMethod.both')}
                    {!chatSSOEnabled && <Lock className="h-3 w-3 text-amber-600 dark:text-amber-400" />}
                  </div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">{t('admin.security.authMethod.bothDescription')}</div>
                </div>
              </label>
            </div>
          </section>
          {/* Two-Factor Authentication Configuration */}
          {authMethod !== 'PUBLIC' && (
            <section className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800 p-6 relative">
              {!chat2FAEnabled && <div className="absolute inset-0 bg-white/50 dark:bg-gray-900/50 rounded-lg" />}

              <div className="flex items-center gap-2 mb-4 relative z-10">
                <Smartphone className="h-5 w-5 text-gray-400" />
                <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">{t('admin.security.twoFactor.title')}</h2>
                {!chat2FAEnabled && (
                  <span className="text-xs px-1.5 py-0.5 bg-purple-100 dark:bg-purple-900/20 text-purple-700 dark:text-purple-400 rounded">
                    {t('admin.sidebar.badges.proEnterprise')}
                  </span>
                )}
              </div>

              {!chat2FAEnabled && (
                <div className="mb-4 p-4 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 border border-amber-200 dark:border-amber-800 rounded-lg relative z-20">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 p-2 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
                      <Smartphone className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                    </div>
                    <div className="flex-1">
                      <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-1">{t('upgradeCTA.twoFactor.title')}</h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">{t('upgradeCTA.twoFactor.description')}</p>
                      <div className="flex items-center gap-3">
                        <a
                          href={`${
                            process.env.NEXT_PUBLIC_VECTORIZE_BASE_URL || 'https://app.vectorize.io'
                          }/organization/${organizationId}/admin/billing/changeplan`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white font-medium rounded-lg transition-colors text-sm"
                        >
                          {t('upgradeCTA.upgradeToPro')}
                          <ChevronRight className="h-4 w-4" />
                        </a>
                        <span className="text-sm text-gray-500 dark:text-gray-400">{t('upgradeCTA.twoFactor.features')}</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className={`space-y-6 ${!chat2FAEnabled ? 'relative z-10' : ''}`}>
                {/* 2FA Required Toggle */}
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-gray-900 dark:text-gray-100">{t('admin.security.twoFactor.requireLabel')}</p>
                      {saving2FA && chat2FAEnabled && <Loader2 className="h-3 w-3 animate-spin text-gray-500" />}
                    </div>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{t('admin.security.twoFactor.requireDescription')}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => chat2FAEnabled && handleToggle2FA(!twoFactorConfig.required)}
                    disabled={saving2FA || !chat2FAEnabled}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors 
                               disabled:opacity-50 disabled:cursor-not-allowed
                               ${twoFactorConfig.required && chat2FAEnabled ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-700'}`}
                  >
                    <span className="sr-only">{t('admin.security.twoFactor.requireLabel')}</span>
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform
                                 ${twoFactorConfig.required && chat2FAEnabled ? 'translate-x-6' : 'translate-x-1'}`}
                    />
                  </button>
                </div>

                {/* Remember Device Options - Only show when 2FA is enabled */}
                {twoFactorConfig.required && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                      {t('admin.security.twoFactor.rememberDeviceLabel')}
                    </label>

                    <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                      {[0, 1, 7, 14, 30].map((days) => (
                        <button
                          key={days}
                          type="button"
                          onClick={() => chat2FAEnabled && handleSetRememberDays(days)}
                          disabled={saving2FA || !chat2FAEnabled}
                          className={`px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 
                                     disabled:opacity-50 disabled:cursor-not-allowed
                                     ${
                                       twoFactorConfig.rememberDeviceDays === days && chat2FAEnabled
                                         ? 'bg-blue-600 text-white shadow-md scale-105'
                                         : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 hover:scale-105'
                                     }`}
                        >
                          {days === 0
                            ? t('admin.security.twoFactor.never')
                            : `${days} ${days === 1 ? t('admin.security.twoFactor.day') : t('admin.security.twoFactor.days')}`}
                        </button>
                      ))}
                    </div>

                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-3">
                      {twoFactorConfig.rememberDeviceDays === 0
                        ? t('admin.security.twoFactor.alwaysRequire')
                        : t('admin.security.twoFactor.rememberDescription', { days: twoFactorConfig.rememberDeviceDays })}
                    </p>
                  </div>
                )}
              </div>
            </section>
          )}
          {/* SAML Configuration */}
          <section className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800 p-6 relative">
            {!chatSSOEnabled && <div className="absolute inset-0 bg-white/50 dark:bg-gray-900/50 rounded-lg" />}

            <div className="flex items-center gap-2 mb-4 relative z-10">
              <Key className="h-5 w-5 text-gray-400" />
              <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">{t('admin.security.saml.title')}</h2>
              {!chatSSOEnabled && (
                <span className="text-xs px-1.5 py-0.5 bg-purple-100 dark:bg-purple-900/20 text-purple-700 dark:text-purple-400 rounded">
                  {t('admin.sidebar.badges.enterprise')}
                </span>
              )}
            </div>

            {!chatSSOEnabled && (
              <div className="mb-4 p-4 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 border border-amber-200 dark:border-amber-800 rounded-lg relative z-20">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 p-2 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
                    <Shield className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                  </div>
                  <div className="flex-1">
                    <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-1">{t('upgradeCTA.sso.title')}</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">{t('upgradeCTA.sso.description')}</p>
                    <div className="flex items-center gap-3">
                      <a
                        href="https://calendly.com/d/cnbp-y7b-m77"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white font-medium rounded-lg transition-colors text-sm"
                      >
                        Talk to Sales
                        <ChevronRight className="h-4 w-4" />
                      </a>
                      <span className="text-sm text-gray-500 dark:text-gray-400">{t('upgradeCTA.sso.features')}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Service Provider Info */}
            <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-900/50 rounded-lg">
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">{t('admin.security.saml.serviceProviderInfo')}</h3>
              <div className="space-y-2 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-gray-600 dark:text-gray-400">{t('admin.security.saml.entityId')}</span>
                  <div className="flex items-center gap-2">
                    <code className="text-xs bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded">{getSamlEntityId(agentId)}</code>
                    <button
                      onClick={() => chatSSOEnabled && copyEntityId()}
                      disabled={!chatSSOEnabled}
                      className="p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Copy className="h-3 w-3" />
                    </button>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600 dark:text-gray-400">{t('admin.security.saml.acsUrl')}</span>
                  <div className="flex items-center gap-2">
                    <code className="text-xs bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded">{getSamlCallbackUrl(agentId)}</code>
                    <button
                      onClick={() => chatSSOEnabled && copyAcsUrl()}
                      disabled={!chatSSOEnabled}
                      className="p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Copy className="h-3 w-3" />
                    </button>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600 dark:text-gray-400">{t('admin.security.saml.metadataUrl')}</span>
                  <div className="flex items-center gap-2">
                    <code className="text-xs bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded">
                      {getSamlBaseUrl()}/api/chat/agents/{agentId}/saml/metadata
                    </code>
                    <button
                      onClick={() => chatSSOEnabled && copyMetadataUrl()}
                      disabled={!chatSSOEnabled}
                      className="p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Copy className="h-3 w-3" />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Identity Provider Settings */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('admin.security.saml.ssoUrlLabel')}</label>
                <input
                  type="url"
                  value={samlConfig.ssoUrl}
                  onChange={(e) => chatSSOEnabled && setSamlConfig({ ...samlConfig, ssoUrl: e.target.value })}
                  disabled={!chatSSOEnabled}
                  placeholder="https://idp.example.com/sso/saml"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('admin.security.saml.issuerLabel')}</label>
                <input
                  type="text"
                  value={samlConfig.issuer}
                  onChange={(e) => chatSSOEnabled && setSamlConfig({ ...samlConfig, issuer: e.target.value })}
                  disabled={!chatSSOEnabled}
                  placeholder="https://idp.example.com"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('admin.security.saml.certificateLabel')}</label>
                <div className="relative">
                  <textarea
                    value={samlConfig.certificate}
                    onChange={(e) => chatSSOEnabled && setSamlConfig({ ...samlConfig, certificate: e.target.value })}
                    disabled={!chatSSOEnabled}
                    placeholder="-----BEGIN CERTIFICATE-----&#10;...&#10;-----END CERTIFICATE-----"
                    rows={showCertificate ? 10 : 3}
                    className={`w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 font-mono text-xs ${
                      !chatSSOEnabled ? 'select-none cursor-not-allowed opacity-50' : ''
                    }`}
                    style={!chatSSOEnabled ? { userSelect: 'none', WebkitUserSelect: 'none', MozUserSelect: 'none' } : undefined}
                  />
                  <button
                    type="button"
                    onClick={() => chatSSOEnabled && setShowCertificate(!showCertificate)}
                    disabled={!chatSSOEnabled}
                    className="absolute top-2 right-2 p-1 hover:bg-gray-100 dark:hover:bg-gray-600 rounded"
                  >
                    {showCertificate ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('admin.security.saml.domainsLabel')}</label>
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={domainInput}
                    onChange={(e) => chatSSOEnabled && setDomainInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && chatSSOEnabled && (e.preventDefault(), addDomain())}
                    disabled={!chatSSOEnabled}
                    placeholder="example.com"
                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
                  />
                  <button
                    type="button"
                    onClick={() => chatSSOEnabled && addDomain()}
                    disabled={!chatSSOEnabled}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg"
                  >
                    {t('admin.security.saml.addDomain')}
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {samlConfig.allowedDomains.map((domain) => (
                    <span key={domain} className="inline-flex items-center gap-1 px-3 py-1 bg-gray-100 dark:bg-gray-700 rounded-full text-sm">
                      <Globe className="h-3 w-3" />
                      {domain}
                      <button
                        onClick={() => chatSSOEnabled && removeDomain(domain)}
                        disabled={!chatSSOEnabled}
                        className="ml-1 hover:text-red-500 disabled:cursor-not-allowed"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
                {samlConfig.allowedDomains.length === 0 && (
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{t('admin.security.saml.noRestrictions')}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('admin.security.saml.defaultRoleLabel')}</label>
                <select
                  value={samlConfig.defaultRole}
                  onChange={(e) => chatSSOEnabled && setSamlConfig({ ...samlConfig, defaultRole: e.target.value as 'user' | 'admin' })}
                  disabled={!chatSSOEnabled}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
                >
                  <option value="user">{t('admin.users.roles.user')}</option>
                  <option value="admin">{t('admin.users.roles.admin')}</option>
                </select>
              </div>

              {/* Attribute Mapping */}
              <div>
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{t('admin.security.saml.attributeMapping')}</h3>
                <div className="space-y-2">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">{t('admin.security.saml.emailAttribute')}</label>
                      <input
                        type="text"
                        value={samlConfig.attributeMapping.email}
                        onChange={(e) =>
                          chatSSOEnabled &&
                          setSamlConfig({
                            ...samlConfig,
                            attributeMapping: { ...samlConfig.attributeMapping, email: e.target.value },
                          })
                        }
                        disabled={!chatSSOEnabled}
                        className="w-full px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-700 rounded bg-white dark:bg-gray-800"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">{t('admin.security.saml.firstNameAttribute')}</label>
                      <input
                        type="text"
                        value={samlConfig.attributeMapping.firstName}
                        onChange={(e) =>
                          chatSSOEnabled &&
                          setSamlConfig({
                            ...samlConfig,
                            attributeMapping: { ...samlConfig.attributeMapping, firstName: e.target.value },
                          })
                        }
                        disabled={!chatSSOEnabled}
                        className="w-full px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-700 rounded bg-white dark:bg-gray-800"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">{t('admin.security.saml.lastNameAttribute')}</label>
                      <input
                        type="text"
                        value={samlConfig.attributeMapping.lastName}
                        onChange={(e) =>
                          chatSSOEnabled &&
                          setSamlConfig({
                            ...samlConfig,
                            attributeMapping: { ...samlConfig.attributeMapping, lastName: e.target.value },
                          })
                        }
                        disabled={!chatSSOEnabled}
                        className="w-full px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-700 rounded bg-white dark:bg-gray-800"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="col-span-2">
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('admin.security.saml.avatarAttribute')}
                      </label>
                      <input
                        type="text"
                        value={samlConfig.attributeMapping.avatar || ''}
                        onChange={(e) =>
                          chatSSOEnabled &&
                          setSamlConfig({
                            ...samlConfig,
                            attributeMapping: { ...samlConfig.attributeMapping, avatar: e.target.value },
                          })
                        }
                        disabled={!chatSSOEnabled}
                        placeholder="picture, photo, thumbnailPhoto, avatar"
                        className="w-full px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-700 rounded bg-white dark:bg-gray-800"
                      />
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{t('admin.security.saml.avatarDescription')}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Save Button for SAML Config */}
              <div className="flex justify-end mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                <button
                  onClick={handleSaveSaml}
                  disabled={!hasSamlChanges || savingSaml || !chatSSOEnabled}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg
                             transition-colors disabled:opacity-50 disabled:cursor-not-allowed
                             flex items-center gap-2"
                >
                  {savingSaml ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      {t('admin.security.saving')}
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4" />
                      {t('admin.security.saveButton')}
                    </>
                  )}
                </button>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
